import BaseService from '../base-service';
import StandardResponse from '../../models/standard-response';
import { IOrganisationList, IOrganisationDetail, IOrganisationFilter, ICountryData } from 'models/organisation';
import { IOrganisationValidationFormState } from 'types/stateTypes';
import { IDriverPromotionList } from 'models/driver-promotion';

export interface FindQuery {
    text: string | undefined;
    containerId?: string;
    countries: string;
}
export interface Item {
    Id: string;
    Description: string;
    Type: 'Address' | 'Postcode' | 'Residential' | 'Street' | 'Container';
    Text: string;
    Highlight: string;
    Label: string;
}
export default class OrganisationService {
    getDriverPromotionList = async (): Promise<StandardResponse<IDriverPromotionList>> => {
        try {
            const data = { status: ['ENABLE'], for_organisations: true, order: [['name', 'asc']] };
            const res = await BaseService({
                url: `/v1/admin/driver/promotions/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<IDriverPromotionList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getOrganisationList = async (data: IOrganisationFilter): Promise<StandardResponse<IOrganisationList>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<IOrganisationList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    getOrganisation = async (id: number): Promise<StandardResponse<IOrganisationDetail>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/${id}`,
                method: 'GET',
            });
            return new StandardResponse<IOrganisationDetail>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    addOrganisation = async (data: IOrganisationValidationFormState): Promise<StandardResponse<{ id: number }>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations`,
                method: 'post',
                data,
            });
            return new StandardResponse<{ id: number }>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    editOrganisation = async (
        data: IOrganisationValidationFormState,
        id: number,
    ): Promise<StandardResponse<{ id: number }>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/${id}`,
                method: 'put',
                data,
            });
            return new StandardResponse<{ id: number }>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    deleteOrganisation = async (id: number): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/${id}`,
                method: 'delete',
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    updateOrganisationStatus = async (data: { active: boolean }, id: number): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/${id}`,
                method: 'patch',
                data,
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    getAPIPermission = async (organisation_id: number): Promise<StandardResponse<IOrganisationDetail>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/api-permissions/${organisation_id}`,
                method: 'GET',
            });
            return new StandardResponse<IOrganisationDetail>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    updateAPIPermission = async (
        data: { fleet_api_ids: number[] },
        organisation_id: number,
    ): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/api-permissions/${organisation_id}`,
                method: 'PUT',
                data,
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    getOrganisationAccountBalanceDetails = async (
        organisation_id: number,
    ): Promise<StandardResponse<IOrganisationList>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/${organisation_id}/account-balance`,
                method: 'GET',
            });
            return new StandardResponse<IOrganisationList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    retrieve = async (id: string): Promise<{ data: { Items?: Item[] } }> => {
        try {
            const res = await BaseService({
                url: '/v1/admin/organisations/address',
                method: 'POST',
                data: {
                    address_key: id,
                },
            });
            return Promise.resolve(res?.data);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    find = async (query: FindQuery): Promise<{ data: { Items?: Item[] } }> => {
        const { text, containerId, countries } = query;
        try {
            const res = await BaseService({
                url: '/v1/admin/organisations/address/search',
                method: 'POST',
                data: {
                    address_text: text,
                    container_key: containerId,
                    countries,
                },
            });
            return Promise.resolve(res?.data);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getCountryList = async (): Promise<{ data: ICountryData[] }> => {
        try {
            const res = await BaseService({
                url: '/v1/admin/organisations/address/search-countries',
                method: 'GET',
            });
            return Promise.resolve(res?.data);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    updateOrganisationsPromotion = async (data: any): Promise<StandardResponse<any>> => {
        try {
            const res = await BaseService({
                url: `/v1/admin/organisations/assign-promotion`,
                method: 'post',
                data,
            });
            return new StandardResponse<any>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
}
